import { Component, ElementRef, inject, OnDestroy, OnInit, ChangeDetectorRef, viewChild } from '@angular/core';
import { AtlasManageService } from '@app/auth/atlas-manage/atlas-manage.service';
import { SharedService } from '@shared/service/shared.service';
import { CourseService } from '@shared/service/course.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { isNil } from 'ng-zorro-antd/core/util';
import { Title } from '@angular/platform-browser';
import { EchartsNode, Node } from '@shared/model/course.models';
import { XKNodesEnum } from '@shared/model/question.models';
import { NzModalService } from 'ng-zorro-antd/modal';
import { RxjsSubscription } from '@common';

@Component({
  selector: 'fl-atlas-manage',
  templateUrl: './atlas-manage.component.html',
  styleUrls: ['./atlas-manage.component.scss'],
  standalone: false,
})
export class AtlasManageComponent implements OnInit, OnDestroy {
  public atlasSrv = inject(AtlasManageService);
  public sharedSrv = inject(SharedService);
  private courseSrv = inject(CourseService);
  private msgSrv = inject(NzMessageService);
  private title = inject(Title);
  private modalSrv = inject(NzModalService);

  // 要添加的知识点名称
  addNodeName: string = '';
  // 编辑模式
  childEditMode: 'normal' | 'add' | 'select' = 'normal';

  index: number = 0;

  isShowAlert: boolean = true;

  isShowAssignParent: boolean = false;

  selectNode: Node = null;

  isEditNodeTitle: boolean = false;

  changeNodeTitle: string = '';

  subs: RxjsSubscription = new RxjsSubscription();

  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  readonly childNodes = viewChild<ElementRef>('nodeList');
  readonly addNodeRef = viewChild<ElementRef>('add_nodes');

  get parentIds() {
    return this.atlasSrv.parentNodes?.map(x => x.key);
  }

  get selectKeys() {
    if (!this.atlasSrv.parentNodes) return [];
    if (this.selectNode) return [this.selectNode.key];
    return [this.atlasSrv.parentNodes[this.atlasSrv.parentNodes?.length - 1].key];
  }

  get isChildSelectVisble() {
    if (!this.atlasSrv.childrenNodes) return false;
    return this.atlasSrv.childrenNodes.filter(item => item.checked).every(x => x.isVisible);
  }

  /**
   * 如果没开启 V2，那么禁用所有按钮操作
   */
  get btnIsDisable(): boolean {
    return !this.atlasSrv.isStartV2 && this.index === 1;
  }

  async ngOnInit() {
    this.title.setTitle('图谱管理');
    await this.atlasSrv.getCategory();
    this.index = this.atlasSrv.isStartV2 ? 1 : 0;
    this.atlasSrv.selectCategory = this.atlasSrv.isStartV2 ? XKNodesEnum.GLOBAL : XKNodesEnum.TRADITION;
    await this.atlasSrv.loadNodes();
    this.atlasSrv.currentNode = this.atlasSrv.nodeList;
    this.atlasSrv.childrenNodes = this.atlasSrv.findChildNode(1000000);

    // 监听图表 label点击事件
    this.subs.subscriptions = this.atlasSrv.clickNodes.subscribe(node => {
      if (!isNil(node)) this.tapChildNode(node, true);
    });

    this.subs.subscriptions = this.atlasSrv.expandNode$.subscribe(node => {
      if (!isNil(node)) {
        this.setRelation(node);
        // OnPush：事件源自 canvas（非本组件模板事件），需主动标记
        this.cdr.markForCheck();
      }
    });
    // OnPush：await 服务加载后模板 getter（selectKeys/parentIds 等）需主动标记刷新
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.atlasSrv.expendNodeSet.clear();
  }
  changeMode(mode) {
    this.childEditMode = mode;

    setTimeout(() => {
      this.childNodes().nativeElement.scrollTo({ top: this.addNodeRef().nativeElement.offsetTop, behavior: 'smooth' });
    }, 0);
  }

  /**
   * 设置关联关系
   */
  setRelation(node: EchartsNode) {
    this.atlasSrv.currentNode = node;
    this.atlasSrv.childrenNodes = this.atlasSrv.findChildNode(node.key);
    this.atlasSrv.parentNodes = [
      ...this.atlasSrv.findParentNode(this.atlasSrv.nodeList, node.key),
      this.atlasSrv.currentNode,
    ];
  }

  tapChildNode(node, isCanTap: boolean) {
    if (this.childEditMode !== 'normal') this.childEditMode = 'normal';
    // 如果不可见
    if (!this.atlasSrv.currentNode.isVisible && !isCanTap) return false;
    if (node.key === this.atlasSrv.currentNode.key) return false;
    this.setRelation(node);
    if (!isCanTap) {
      this.atlasSrv.updateNodes.next(this.atlasSrv.setChildrenNodesCollapsed());
    }
    // OnPush：由 canvas 订阅回调触发（非模板事件），需主动标记
    this.cdr.markForCheck();
    // this.atlasSrv.loadNodes(true);
  }

  /**
   * 添加新的知识点
   */
  async addNode() {
    let parentId = null;
    if (this.atlasSrv.currentNode.key !== 1000000) {
      parentId = this.atlasSrv.currentNode.key;
    }
    try {
      const result = await this.courseSrv.addGraphNode(this.sharedSrv.user.subject_id, parentId, this.addNodeName);
      this.msgSrv.success('操作成功');
      this.addNodeName = '';
      this.childEditMode = 'normal';
      await this.atlasSrv.loadNodes(true);
      this.atlasSrv.childrenNodes = this.atlasSrv.findChildNode(this.atlasSrv.currentNode.key);
      // OnPush：await 续体需主动标记
      this.cdr.markForCheck();
    } catch (e) {
      this.msgSrv.error(e.detail ?? '添加失败');
    }
  }

  async batchDisableNode() {
    if (this.atlasSrv.currentNode.key === 1000000) {
      this.msgSrv.error('不可禁用顶级知识点');
      return;
    }
    try {
      const nodeIds = this.atlasSrv.childrenNodes.filter(item => item.checked);
      await this.courseSrv.setNodeVisible(this.sharedSrv.user.subject_id, {
        hide: nodeIds[0].isVisible,
        nodeIds: nodeIds.map(x => x.key),
      });
      this.msgSrv.success('操作成功');
      this.childEditMode = 'normal';
      await this.atlasSrv.loadNodes(true);
      this.atlasSrv.childrenNodes = this.atlasSrv.findChildNode(this.atlasSrv.currentNode.key);
      this.cdr.markForCheck();
    } catch (e) {
      this.msgSrv.error(e.detail ?? '操作失败，请重试');
    }
  }

  /**
   * 禁用知识点
   */
  async forbiddenNode(visible: boolean) {
    if (this.atlasSrv.currentNode.key === 1000000) {
      this.msgSrv.error('不可禁用顶级知识点');
      return;
    }
    try {
      const nodeIds = [this.atlasSrv.currentNode];
      await this.courseSrv.setNodeVisible(this.sharedSrv.user.subject_id, {
        hide: visible,
        nodeIds: nodeIds.map(x => x.key),
      });
      this.msgSrv.success('操作成功');
      // nodeIds.forEach(x => {
      //   // 如果是禁用操作，那么将子节点全部禁用。
      //   if (x.isVisible) {
      //     this.atlasSrv.setChildrenNodesVisible(x, !x.isVisible);
      //   } else {
      //     x.isVisible = !x.isVisible;
      //     this.atlasSrv.findAndModifyTree(this.atlasSrv.nodeList, x.key, x, 'edit');
      //   }
      // });
      this.childEditMode = 'normal';
      await this.atlasSrv.loadNodes(true);
      this.atlasSrv.childrenNodes = this.atlasSrv.findChildNode(this.atlasSrv.currentNode.key);
      this.atlasSrv.currentNode.isVisible = !this.atlasSrv.currentNode.isVisible;
      this.cdr.markForCheck();
    } catch (e) {
      this.msgSrv.error(e.detail ?? '操作失败，请重试');
    }
  }

  changeDisable(tag: boolean) {
    this.atlasSrv.isDisableBtn = tag;
    const list = JSON.parse(JSON.stringify(this.atlasSrv.metaNodes));
    const formatData = graph => {
      if (
        (this.atlasSrv.parentNodes && this.atlasSrv.parentNodes.map(x => Number(x.id)).includes(Number(graph.id))) ||
        this.atlasSrv.expendNodeSet.has(graph.id)
      ) {
        graph.collapsed = false;
      }
      if (graph.children) {
        if (!tag) graph.children = graph.children.filter(x => x.isVisible);
        graph.children.forEach(x => formatData(x));
      }
    };
    formatData(list);
    if (!tag) {
      this.atlasSrv.updateNodes.next(list);
    } else {
      this.atlasSrv.updateNodes.next(list);
    }
  }

  changeLevel(level: number) {
    this.atlasSrv.expendNodeSet.clear();
    const list = JSON.parse(JSON.stringify(this.atlasSrv.nodeList));
    const formatData = (graph: EchartsNode, subLevel: number = 0) => {
      if (subLevel <= level) {
        graph.collapsed = false;
        this.atlasSrv.expendNodeSet.add(graph.id);
      } else {
        graph.collapsed = true;
      }
      if (graph.children) {
        graph.children.forEach(x => formatData(x, subLevel + 1));
      }
    };
    formatData(list);
    this.atlasSrv.updateNodes.next(list);
  }

  onTabChange(index: number) {
    this.index = index;
    switch (index) {
      case 0:
        if (this.atlasSrv.selectCategory !== XKNodesEnum.TRADITION) {
          this.atlasSrv.selectCategory = XKNodesEnum.TRADITION;
          this.atlasSrv.loadNodes(false);
        }
        break;
      case 1:
        if (this.atlasSrv.selectCategory !== XKNodesEnum.GLOBAL) {
          this.atlasSrv.selectCategory = XKNodesEnum.GLOBAL;
          this.atlasSrv.loadNodes(false);
        }
        break;
    }
  }
  async startV2() {
    // const id = this.msgSrv.loading('').messageId;
    if (this.atlasSrv.expendNodeSet.size > 0) this.atlasSrv.expendNodeSet.clear();
    try {
      const result = await this.courseSrv.putCategory(this.sharedSrv.user.subject_id, XKNodesEnum.GLOBAL);
      this.msgSrv.success('操作成功');
      this.atlasSrv.getNodes();
      // OnPush：await 续体需主动标记（isStartV2 变化影响按钮禁用态）
      this.cdr.markForCheck();
    } catch (e) {
      this.msgSrv.error('获取版本列表失败');
    } finally {
      // this.msgSrv.remove(id);
    }
  }

  clickNode(node) {
    this.selectNode = node.node.origin;
  }

  async removeNode() {
    if (!this.selectNode) {
      this.msgSrv.error('未选择节点');
      return;
    }
    if (this.selectNode.disabled) {
      this.msgSrv.error('此节点无法被使用');
      return;
    }
    const id = this.msgSrv.loading('').messageId;
    try {
      const params = {
        parentId: this.selectNode.id,
      };
      const result = await this.courseSrv.putNodeInfo(
        this.sharedSrv.user.subject_id,
        this.atlasSrv.currentNode.id,
        params
      );
      this.isShowAssignParent = false;
      this.msgSrv.success('操作成功');
      this.atlasSrv.loadNodes(true);
      // OnPush：await 续体需主动标记（关联父级面板关闭依赖此渲染）
      this.cdr.markForCheck();
    } finally {
      this.msgSrv.remove(id);
    }
  }

  /**
   * 关联父级
   */
  openAssignParent() {
    this.isShowAssignParent = true;
    if (this.selectKeys.length > 0) this.atlasSrv.disabledSelectKey(this.selectKeys[0]);
  }

  async changeTitle() {
    if (this.changeNodeTitle === '') {
      this.msgSrv.error('请输入节点名称');
      return;
    }
    // const id = this.msgSrv.loading('').messageId;
    try {
      const result = await this.courseSrv.putNodeInfo(this.sharedSrv.user.subject_id, this.atlasSrv.currentNode.id, {
        title: this.changeNodeTitle,
      });
      this.msgSrv.success('操作成功');
      this.isEditNodeTitle = false;
      this.atlasSrv.currentNode.title = this.changeNodeTitle;
      this.changeNodeTitle = '';
      this.atlasSrv.loadNodes(true);
      this.cdr.markForCheck();
    } catch (e) {
      this.msgSrv.error('修改失败');
    } finally {
      // this.msgSrv.remove(id);
    }
  }
}
